import { useContext, useEffect, useState } from 'react';
import cn from 'clsx';
import { useChannelActionContext, useChannelStateContext } from 'stream-chat-react';
import { makeStyles } from '@material-ui/styles';
import { CloseFill, Text, Theme } from '@bizzabo/design-system';
import { useChatClient } from '@bizzabo/chat-core';
import SessionChatCtx from '../../context/SessionChatCtx';
import useTypeTracking from '../../analytics';

type Props = {
    channelTitle: string;
    onCloseThread?: () => void;
};

const ChannelHeader: React.FC<Props> = ({ channelTitle, onCloseThread }) => {
    const classes = useStyles();
    const { channel, messages, thread } = useChannelStateContext();
    const { closeThread } = useChannelActionContext();
    const sessionChatContext = useContext(SessionChatCtx);
    const { trackClickEvent } = useTypeTracking();
    const { client } = useChatClient();
    const [hasNewMessages, setHasNewMessages] = useState<boolean>(false);

    useEffect(() => sessionChatContext?.onThreadView?.(!!thread), [thread]);

    useEffect(() => {
        const channelListener = channel.on('message.new', event => {
            if (client?.user?.id !== event.user?.id) {
                setHasNewMessages(true);
            }
        });
        return () => {
            channelListener.unsubscribe();
        };
    }, []);

    const handleBackClick = e => {
        closeThread(e);
        trackClickEvent({
            buttonType: 'CHAT_THREAD',
            subCategory: 'BACK_TO_CONVERSATIONS',
        });
        onCloseThread?.();
        setHasNewMessages(false);
    };

    const messagesCount = channel.state.messages.length;
    const headerSubtext =
        messagesCount === 1
            ? `${messagesCount} Live Conversation`
            : `${messagesCount} Live Conversations`;

    return (
        <div className={cn(classes.header, 'channel-header-container', { thread })}>
            {!thread && (
                <Text variant="t40" className={cn(classes.title, 'channel-header-title')}>
                    {channelTitle}
                </Text>
            )}
            {!thread && !!messages.length && (
                <Text
                    className={cn(classes.headerSubtitle, 'channel-header-sub-title')}
                    variant="t81"
                >
                    {headerSubtext}
                </Text>
            )}
            {thread && (
                <div className={classes.threadHeader}>
                    <div className={classes.closeBtn} onClick={handleBackClick}>
                        <CloseFill className={classes.closeIcon} />
                        <span>Thread</span>
                        {hasNewMessages && <span className={classes.notification} />}
                    </div>
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles(
    (theme: Theme) => ({
        header: {
            padding: '12px 32px 4px',
            borderBottom: `solid 1px ${theme.palette.greys[400]}`,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            '&.thread': {
                padding: '0 32px',
                position: 'relative',
                height: 70,
                minHeight: 60,
                [theme.breakpoints.down('sm')]: {
                    minHeight: 46,
                    height: 46,
                },
            },
        },
        title: {
            marginBottom: '4px',
            flex: '0 0 100%',
            lineHeight: 'inherit',
        },
        headerSubtitle: {
            color: theme.palette.greys[500],
        },
        backToPanelContainer: {
            display: 'none',
        },
        closeBtn: {
            ...theme.typography.t80,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            borderBottom: 'none',
            height: 60,
            paddingTop: 42,
            paddingBottom: 18,
            '& svg': {
                marginRight: 6,
            },
            [theme.breakpoints.down('sm')]: {
                height: 'auto',
                padding: '13px 0 0',
            },
        },
        closeIcon: {
            width: 20,
            height: 20,
        },
        threadHeader: {
            position: 'absolute',
            borderRadius: '33px 33px 0 0',
            backgroundColor: theme.palette.greys[100],
            height: 60,
            padding: '0 24px',
            width: '100%',
            top: 0,
            left: 0,
            [theme.breakpoints.down('sm')]: {
                height: 46,
            },
        },
        notification: {
            width: 7,
            height: 7,
            marginLeft: 3,
            marginBottom: 7,
            backgroundColor: theme.palette.notification,
            borderRadius: '50%',
        },
    }),
    {
        classNamePrefix: 'ChannelHeader',
    }
);

export default ChannelHeader;
